import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { ErrorMessage } from "formik";
import Button from "../../../components/ui/Button";
import { Dropzone } from "../../../components/Dropzone/Dropzone";
import instance from "../../../api";

export interface FormDropzoneFieldProps {
  field: any;
  label: string;
  readOnly?: boolean;
  onFileUploadEnd(file: File): void;
  file: File;
  resetFile(): void;
  fileInstructions?: string;
  maximumFileSizeInMB?: number;
  maximumNoOfFiles?: number;
  resetButtonText: string;
  downloadableFile?: boolean;
  downloadButtonText?: string;
  fileType?: string;
  dropzoneText?: string;
  uploadIcon?: string;
  warningIcon?: string;
  showUploadedImage?: any;
  uploadedImageUrl?: string;
  showReset?: boolean;
}

const ErrorM: React.FC<{ name: string }> = (props) => {
  const { errors, touched, submitCount } = useFormikContext<any>();
  const error = errors[props.name];
  const isTouched = touched[props.name];

  // Show error if field is touched OR if form has been submitted (submitCount > 0)
  if ((isTouched || submitCount > 0) && error) {
    return <div className="ErrorLabel">{error}</div>;
  }

  return null;
};

function UploadCSV<T>(props: FormDropzoneFieldProps) {
  const formikContext = useFormikContext();
  const [hasUploadedImage, setHasUploadedImage] = useState<boolean>(false);
  const [showWarningMessage, setShowWarningMessage] = React.useState<string | null>();

  function setFileType(chosen_file: File) {
    if (chosen_file) {
      if (!props.fileType) {
        if (isImage(chosen_file)) {
          return "image";
        } else if (isVideo(chosen_file)) {
          return "video";
        } else if (isCSV(chosen_file)) {
          return "csv";
        } else if (isDoc(chosen_file)) {
          return "document";
        }
      } else {
        return props.fileType.toLowerCase();
      }
    }
  }

  function downloadUploadedFile() {
    const blob = new Blob([props.file], { type: props.file.type });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.setAttribute("download", props.file.name);
    document.body.appendChild(link);
    link.click();
    setTimeout(function () {
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    }, 100);
  }

  const onDrop = (acceptedFiles: File[]) => {
    validateFiles(acceptedFiles);
  };

  function validateFiles(acceptedFiles: File[]) {
    let filesValPassed = true;

    if (filesValPassed && !acceptedFiles[0]) {
      setDropzoneWarningMsg("An error occured, please try again");
      filesValPassed = false;
    }

    if (filesValPassed && props.maximumNoOfFiles && acceptedFiles.length > props.maximumNoOfFiles) {
      const pluralTxtMsg =
        props.maximumNoOfFiles > 1
          ? "Please only upload up to " + props.maximumNoOfFiles + " files"
          : "Please only upload one file";
      setDropzoneWarningMsg(pluralTxtMsg);
      filesValPassed = false;
    }

    // if (filesValPassed && props.fileType) {
    if (filesValPassed) {
      let isFileTypeAllowed = false;

      if (
        // setFileType(acceptedFiles[0]) === "text/csv" ||
        setFileType(acceptedFiles[0]) === "csv" ||
        setFileType(acceptedFiles[0]) ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        isFileTypeAllowed = true;
      }

      if (!isFileTypeAllowed) {
        setDropzoneWarningMsg("Please upload only CSV files");
        filesValPassed = false;
      }
    }

    if (
      filesValPassed &&
      props.maximumFileSizeInMB &&
      acceptedFiles[0].size / 1000000 >= props.maximumFileSizeInMB
    ) {
      setDropzoneWarningMsg("File exceeds " + props.maximumFileSizeInMB + "MB size limit");
      filesValPassed = false;
    }

    completeFileValidation(filesValPassed, acceptedFiles);
  }

  const setDropzoneWarningMsg = (warning_str: string) => {
    setShowWarningMessage(warning_str);
    setTimeout(() => {
      setShowWarningMessage(null);
    }, 2500);
  };

  function completeFileValidation(file_validated: boolean, acceptedFiles: File[]) {
    if (file_validated) {
      // console.log("acceptedFiles: ", acceptedFiles);
      // console.log("(props.field: ", props.field);
      props.onFileUploadEnd(acceptedFiles[0]);
      formikContext.setFieldValue(props.field, acceptedFiles[0]);
      formikContext.setFieldValue(props.field + "Modified", true);
    } else {
      props.resetFile();
      formikContext.setFieldValue(props.field, undefined);
      return;
    }
  }

  return (
    <div className="UploadCSV">
      <div className="DataProcessing">
        (Note that it may take a few minutes for documents to be uploaded...)
      </div>

      <Dropzone
        uploadedFile={props.file}
        onDrop={onDrop}
        fileType="text/csv"
        viewOnly={props.readOnly ?? false}
        dropzoneTitle={props.dropzoneText}
        showWarningMessage={showWarningMessage}
        name={props.file && props.file.name ? props.file.name : "Uploaded file"}
        // hasUploadedImage={hasUploadedImage}
        // uploadedImageUrl={props.uploadedImageUrl}
      />
      <div className="container">
        {props.fileInstructions && (
          <span className="FileInstructions">{props.fileInstructions}</span>
        )}

        {props.showReset && (
          <React.Fragment>
            {/* (props.file || hasUploadedImage) */}
            <Button
              className={`${props.file || hasUploadedImage ? "Btn" : "BtnReset"}`}
              type="button"
              variety="full"
              onClick={async () => {
                props.resetFile();
                setHasUploadedImage(false);
                await formikContext.setFieldValue(props.field, undefined, true);
                formikContext.setFieldTouched(props.field, true, true);
                formikContext.setFieldValue(props.field + "Modified", true);
              }}
            >
              {props.resetButtonText ?? ""}
            </Button>
          </React.Fragment>
        )}
      </div>
      {props.downloadableFile && (
        <React.Fragment>
          <Button
            className="Btn"
            type="button"
            variety="full"
            onClick={() => {
              downloadUploadedFile();
            }}
          >
            {props.downloadButtonText ?? ""}
          </Button>
        </React.Fragment>
      )}
      <ErrorM name={props.field} />
    </div>
  );
}

export default UploadCSV;

const isImage = (file: File) => file.type.search("image") > -1;
const isVideo = (file: File) => file.type.search("video") > -1;
const isCSV = (file: File) =>
  file.type.search("text/csv") > -1 ||
  file.type.search("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") > -1;
const isDoc = (file: File) => {
  if (
    file.type === "application/pdf" ||
    file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    file.type === "application/msword"
  ) {
    return true;
  } else {
    return false;
  }
};
